/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Common utils for the app
 */
import _ from 'lodash';
import log from 'loglevel';
import { transliterate } from 'transliteration';

import { AntClass } from '../constants/antd';
import { PLATFORMS } from '../constants/index';

/**
 * Finds tenant name
 * @returns {string} tenantName
 */
const getTenantName = (): string => {
	const hostname = globalThis?.location?.hostname;
	const tenant = _.first(_.split(hostname, '.'));
	return (tenant !== 'localhost' ? tenant : DEV_REALM) || 'test1';
};

/**
 * Finds keycloak realm name as per tenant
 * @returns {string} tenantName
 */
const getRealmName = (): string => {
	const tenantName = getTenantName();
	return tenantName ? `veritable_${tenantName}` : '';
};

/**
 * get browser platform / os
 */
const getPlatform = (): UniversalSearch.Platform => {
	if (_.startsWith(_.toLower(navigator?.platform), PLATFORMS.mac)) {
		return PLATFORMS.mac;
	}
	return PLATFORMS.other;
};

/**
 * translate to ascii
 */
const translateToAscii = (e: React.ChangeEvent<HTMLInputElement>): void => {
	e.target.value = transliterate(e?.target?.value);
};

/**
 * scroll to an element by id
 */
const scrollToElement = (elementID?: string): void => {
	setTimeout(() => {
		if (elementID) {
			const elementClass = 'focused-element';
			const element = document?.getElementById(elementID);
			if (element) {
				element.style.scrollMarginTop = '200px';
				element.classList.add(elementClass);
				element.scrollIntoView({ block: 'start', behavior: 'smooth' });
				setTimeout(() => {
					element.classList.remove(elementClass);
					element.style.scrollMarginTop = '';
				}, 5000);
			}
		}
	}, 500);
};

/**
 * scroll to element by url hash
 */
const scrollToWidgetByHash = (): void => {
	const widgetID = location?.hash?.substring(1);
	if (widgetID) {
		scrollToElement(widgetID);
	}
};

const getValueFromStorage = (key: string, defaultValue: AnyType): AnyType => {
	let val = defaultValue;
	try {
		const stickyValue = globalThis?.localStorage?.getItem(key);
		if (_.isNull(stickyValue)) {
			throw new Error('404');
		}
		val = JSON.parse(stickyValue);
		if (_.isArray(val)) {
			val = _.compact(val);
		}
	} catch (e: AnyType) {
		log.info(`[UseStickyState] ${e?.message === '404' ? 'sticky value is null' : 'Unable to parse sticky state'}`);
		globalThis?.localStorage?.removeItem(key);
	}

	return val;
};

const setValueToStorage = (key: string, value: AnyType): void => {
	localStorage?.setItem(key, JSON.stringify(value));
};

/**
 * scroll to form error field
 */
// TODO
const scrollToFormErrorField = (): void => {
	setTimeout(() => {
		const element = _.first(document?.getElementsByClassName(AntClass.formilyErrorItem));
		if (element) {
			element.scrollIntoView({ block: 'center', behavior: 'smooth' });
		}
	}, 100);
};

/**
 * createBlobFile
 */
const downloadBlobFile = (fileObject: BlobPart, name: string, contentType: string, event: string): string | void => {
	const link = document?.createElement('a');
	const fileCreated = new Blob([fileObject], { type: contentType });

	link.href = URL.createObjectURL(fileCreated);
	if (event === 'download') {
		link.download = name;
		link.click();
	} else {
		return link?.href;
	}
};

/**
 * Checks if a value is non-empty, accounting for booleans and numbers which _.isEmpty treats as empty.
 */
const hasNonEmptyValue = (value: AnyType): boolean => _.isBoolean(value) || _.isNumber(value) || !_.isEmpty(value);

export {
	hasNonEmptyValue,
	getTenantName,
	getRealmName,
	translateToAscii,
	scrollToWidgetByHash,
	setValueToStorage,
	getValueFromStorage,
	scrollToFormErrorField,
	downloadBlobFile,
	getPlatform,
	scrollToElement,
};
